import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

const CHUNK = 200;

/**
 * Сводки числа комментариев по сессиям за диапазон дат (расписание).
 * Возвращает карту sessionId → count для переданного списка id.
 */
export function useSessionCommentSummariesForRange(
  sessionIds: string[],
  rangeKey: { from: string; to: string } | null
) {
  const sortedIds = useMemo(
    () => [...new Set(sessionIds.filter((id) => id && !id.startsWith('temp-')))].sort(),
    [sessionIds]
  );
  const idsKey = sortedIds.join(',');

  const { data, isLoading } = useQuery({
    queryKey: ['sessionCommentSummariesRange', rangeKey?.from, rangeKey?.to, idsKey] as const,
    queryFn: async () => {
      if (sortedIds.length === 0) {
        return { summaries: {} as Record<string, number> };
      }
      const summaries: Record<string, number> = {};
      for (let i = 0; i < sortedIds.length; i += CHUNK) {
        const chunk = sortedIds.slice(i, i + CHUNK);
        const res = await api.sessions.sessionCommentSummaries(chunk);
        for (const [sid, cnt] of Object.entries(res.summaries)) {
          summaries[sid] = cnt;
        }
      }
      for (const id of sortedIds) {
        if (summaries[id] === undefined) summaries[id] = 0;
      }
      return { summaries };
    },
    enabled: Boolean(rangeKey && sortedIds.length > 0),
    staleTime: 10_000,
    placeholderData: (previousData) => previousData,
  });

  const bySessionId = data?.summaries ?? {};

  return {
    bySessionId,
    isLoading: Boolean(rangeKey) && isLoading,
  };
}

/**
 * Сводки по списку id сессий (например попап сессий проекта при open).
 */
export function useSessionCommentSummariesForIds(sessionIds: string[], enabled: boolean) {
  const sortedIds = useMemo(
    () => [...new Set(sessionIds.filter((id) => id && !id.startsWith('temp-')))].sort(),
    [sessionIds]
  );
  const idsKey = sortedIds.join(',');

  const { data, isLoading } = useQuery({
    queryKey: ['sessionCommentSummariesModal', idsKey] as const,
    queryFn: async () => {
      if (sortedIds.length === 0) {
        return { summaries: {} as Record<string, number> };
      }
      const summaries: Record<string, number> = {};
      for (let i = 0; i < sortedIds.length; i += CHUNK) {
        const chunk = sortedIds.slice(i, i + CHUNK);
        const res = await api.sessions.sessionCommentSummaries(chunk);
        for (const [sid, cnt] of Object.entries(res.summaries)) {
          summaries[sid] = cnt;
        }
      }
      for (const id of sortedIds) {
        if (summaries[id] === undefined) summaries[id] = 0;
      }
      return { summaries };
    },
    enabled: enabled && sortedIds.length > 0,
    staleTime: 10_000,
  });

  return {
    bySessionId: data?.summaries ?? {},
    isLoading: enabled && isLoading,
  };
}
